/**
 * ⚠️ MOCK API for the Add-to-Playlist membership/favorites/reorder calls that have no backend RPC
 * yet. Playlist listing is already real (see playlistData.ts `fetchPlaylists`); these remaining
 * methods stay mocked until a dev wires the real RPCs:
 *   getMemberships    → playlists.memberships (by media id)
 *   getPlaylistVideos → playlists.videos (by playlist id)
 *   addToPlaylist     → playlists.add        (media id, playlist id)   — inserts at top
 *   removeFromPlaylist→ playlists.remove     (media id, playlist id)   — membership only
 *   reorderPlaylist   → playlists.reorder    (playlist id, orderedIds)
 *   getFavorites/toggleFavorite → user-pref store (per user)
 *
 * Types live in playlistData.ts (the real data layer).
 */
import { PlaylistVideo } from "./playlistData";

const wait = <T>(value: T, ms = 250): Promise<T> =>
  new Promise((res) => setTimeout(() => res(value), ms));

// playlist ids this video is already in on load — empty by default; a dev fills this from the backend
const MEMBERSHIPS: string[] = [];

let FAVORITES: string[] = []; // per-user pinned-to-top playlists (mock)

export const playlistApi = {
  getMemberships: (_mediaId?: string): Promise<string[]> =>
    wait([...MEMBERSHIPS]),
  getPlaylistVideos: (_playlistId: string): Promise<PlaylistVideo[]> =>
    wait([]),
  addToPlaylist: (_mediaId: string, _playlistId: string): Promise<void> =>
    wait(undefined, 150),
  removeFromPlaylist: (_mediaId: string, _playlistId: string): Promise<void> =>
    wait(undefined, 150),
  reorderPlaylist: (
    _playlistId: string,
    _orderedVideoIds: string[]
  ): Promise<void> => wait(undefined, 150),
  getFavorites: (): Promise<string[]> => wait([...FAVORITES]),
  toggleFavorite: (playlistId: string): Promise<string[]> => {
    FAVORITES = FAVORITES.includes(playlistId)
      ? FAVORITES.filter((id) => id !== playlistId)
      : [...FAVORITES, playlistId];
    return wait([...FAVORITES], 80);
  },
};
